/**
 * ResearchConfirmDialog
 *
 * Confirmation dialog before starting batch research.
 * Shows cost estimate, time estimate, and warnings about API costs.
 *
 * Part of Story 345: Enrichment Opportunities Dashboard
 *
 * Date: 2026-01-06
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, DollarSign, Clock, Package } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  EnrichmentOpportunity,
  startResearch,
  executeResearch,
  formatCost,
  formatEstimatedTime,
} from '@/services/enrichmentOpportunityService';

interface ResearchConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity: EnrichmentOpportunity;
  onConfirm: (opportunityId: string) => void;
}

export function ResearchConfirmDialog({
  open,
  onOpenChange,
  opportunity,
  onConfirm,
}: ResearchConfirmDialogProps) {
  const { toast } = useToast();
  const [isStarting, setIsStarting] = useState(false);

  const estimatedTimeMinutes = Math.ceil(opportunity.productCount / 10) * 2;

  const handleConfirm = async () => {
    setIsStarting(true);
    try {
      // Start research (validates and marks as researching)
      await startResearch(opportunity.id);

      // Execute research (runs in background)
      await executeResearch(opportunity.id);

      toast({
        title: 'Research started',
        description: `Researching ${opportunity.attributeName} for ${opportunity.productCount} products.`,
      });

      onConfirm(opportunity.id);
    } catch (error) {
      console.error('Failed to start research:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start research',
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Research</DialogTitle>
          <DialogDescription>
            Research the <strong>{opportunity.attributeName.replace(/_/g, ' ')}</strong> attribute
            using web search.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Details table */}
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-muted-foreground flex items-center gap-2">
                <Package className="h-4 w-4" />
                Products to research
              </span>
              <span className="font-medium">{opportunity.productCount}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Estimated cost
              </span>
              <span className="font-medium">{formatCost(opportunity.estimatedCost)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Estimated time
              </span>
              <span className="font-medium">{formatEstimatedTime(estimatedTimeMinutes)}</span>
            </div>
          </div>

          {/* Price tier breakdown */}
          <div className="text-sm text-muted-foreground">
            <div className="flex gap-4">
              <span>Premium: {opportunity.premiumCount}</span>
              <span>Mid-tier: {opportunity.midTierCount}</span>
              <span>Entry: {opportunity.entryCount}</span>
            </div>
          </div>

          {/* Warning */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will use web search API credits. Costs are based on the number of products
              researched. Products where data is not found will be marked as N/A.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isStarting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isStarting}>
            {isStarting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <DollarSign className="h-4 w-4 mr-2" />
                Confirm ({formatCost(opportunity.estimatedCost)})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
